import { Combobox } from "@/components/shadcn-custom/ShadcnCombobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useProjectAuditLogs } from "@/features/audit-logs/hooks/useProjectAuditLogs";
import { useUsers } from "@/features/users/hooks/useUsers";
import { formatDateTimeShort } from "@/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpDown, Info, Search } from "lucide-react";
import { useMemo, useState } from "react";

// Helper function to trigger native date/time picker
const triggerPicker = (elementId: string) => {
  const element = document.getElementById(elementId) as HTMLInputElement | null;
  if (element && 'showPicker' in element) {
    (element as any).showPicker();
  }
};

export const Route = createFileRoute("/dashboard/admin/projects/$projectId/audit_logs/")({
  component: RouteComponent,
});

// Mock project data for UI design

function RouteComponent() {
  const { projectId } = Route.useParams();
  // Fetch real audit logs data
  const { auditLogs, loading: auditLogsLoading } = useProjectAuditLogs(projectId);
  const { users } = useUsers();
  // Audit logs state
  const [auditFilterOpen, setAuditFilterOpen] = useState(false);
  const [auditSearchQuery, setAuditSearchQuery] = useState("");
  const [auditFilters, setAuditFilters] = useState({
    entity: "",
    event: "",
    dateRange: "",
  });

  // Transform audit logs to table format
  const transformedAuditLogs = useMemo(() => {
    return auditLogs.flatMap((log) => {
      const getUserName = (authId: string) => {
        if (authId === "unknown" || !authId) return "System";
        const user = users.find((u) => u.uid === authId);
        return user?.name || user?.email || authId;
      };

      const formatValue = (value: any): string => {
        if (value === null || value === undefined) return "-";
        if (typeof value === "string") {
          if (value.length > 50) return value.substring(0, 50) + "...";
          return value;
        }
        if (typeof value === "boolean") return value ? "Yes" : "No";
        if (Array.isArray(value)) return value.length > 0 ? `${value.length} items` : "-";
        if (typeof value === "object") return JSON.stringify(value).substring(0, 50) + "...";
        return String(value);
      };

      const getEventType = (changeType: string, operation: string): string => {
        if (operation === "created") return "Added";
        if (operation === "deleted") return "Deleted";
        if (changeType === "added") return "Added";
        if (changeType === "removed") return "Deleted";
        if (changeType === "modified") return "Updated";
        // Special handling for boolean changes that might indicate enabled/disabled
        return "Updated";
      };

      return log.changes.map((change) => {
        const eventType = getEventType(change.changeType, log.operation);
        // Check if this is an enable/disable operation (boolean toggle)
        const isBooleanToggle =
          typeof change.oldValue === "boolean" &&
          typeof change.newValue === "boolean" &&
          change.oldValue !== change.newValue;

        const finalEventType =
          isBooleanToggle && change.newValue === true
            ? "Enabled"
            : isBooleanToggle && change.newValue === false
              ? "Disabled"
              : eventType;

        return {
          id: `${log.id}-${change.field}`,
          updatedBy: getUserName(log.performedBy.authId),
          entity: change.field,
          event: finalEventType,
          date: formatDateTimeShort(log.timestamp),
          oldValue: formatValue(change.oldValue),
          newValue: formatValue(change.newValue),
          fullOldValue: change.oldValue,
          fullNewValue: change.newValue,
          logId: log.id,
        };
      });
    });
  }, [auditLogs, users]);

  // Filter and search audit logs
  const filteredAuditLogs = useMemo(() => {
    let filtered = transformedAuditLogs;

    // Search filter
    if (auditSearchQuery) {
      const query = auditSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (log) =>
          log.updatedBy.toLowerCase().includes(query) ||
          log.entity.toLowerCase().includes(query) ||
          log.event.toLowerCase().includes(query) ||
          log.oldValue.toLowerCase().includes(query) ||
          log.newValue.toLowerCase().includes(query),
      );
    }

    // Entity filter
    if (auditFilters.entity) {
      filtered = filtered.filter((log) => log.entity === auditFilters.entity);
    }

    // Event filter
    if (auditFilters.event) {
      filtered = filtered.filter((log) => log.event === auditFilters.event);
    }

    // Date filter
    if (auditFilters.dateRange) {
      const filterDate = new Date(auditFilters.dateRange);
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.date);
        return (
          logDate.getFullYear() === filterDate.getFullYear() &&
          logDate.getMonth() === filterDate.getMonth() &&
          logDate.getDate() === filterDate.getDate()
        );
      });
    }

    return filtered;
  }, [transformedAuditLogs, auditSearchQuery, auditFilters]);

  const hasActiveFilters = auditFilters.entity || auditFilters.event || auditFilters.dateRange;

  const getEventBadgeClass = (event: string) => {
    switch (event) {
      case "Updated":
        return "bg-white text-blue-500 border-blue-500";
      case "Added":
        return "bg-white text-green-600 border-green-600";
      case "Deleted":
      case "Disabled":
        return "bg-white text-red-500 border-red-500";
      case "Enabled":
        return "bg-white text-purple-600 border-purple-600";
      default:
        return "bg-white text-gray-600 border-gray-600";
    }
  };

  // Get unique values for filter dropdowns
  const uniqueEntities = useMemo(
    () => Array.from(new Set(transformedAuditLogs.map((log) => log.entity))).sort(),
    [transformedAuditLogs],
  );
  const uniqueEvents = useMemo(
    () => Array.from(new Set(transformedAuditLogs.map((log) => log.event))).sort(),
    [transformedAuditLogs],
  );

  return (
    <div className="flex flex-col min-w-0">
      <div className="mt-4 min-w-0">
        <div className="rounded border border-gray-200 bg-white overflow-hidden">
          {auditLogsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600"></div>
              <span className="ml-3 text-sm text-gray-600">Loading audit logs...</span>
            </div>
          ) : (
            <>
              {/* Search and Filter Row */}
              <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 min-[744px]:flex-row min-[744px]:items-center min-[744px]:justify-between">
                <div className="relative flex-1 min-[744px]:max-w-xs">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
                  <Input
                    placeholder="Search"
                    className="h-9 w-full border-gray-300 pl-9"
                    value={auditSearchQuery}
                    onChange={(e) => setAuditSearchQuery(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => setAuditFilterOpen(!auditFilterOpen)}
                  className="border-muted-foreground text-muted-foreground flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm transition-colors hover:bg-gray-50 min-[744px]:w-auto"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.33336 16.6667C8.33329 16.8215 8.37637 16.9733 8.45777 17.1051C8.53917 17.2368 8.65566 17.3433 8.79419 17.4125L10.4609 18.2458C10.5879 18.3093 10.7291 18.3393 10.8711 18.3329C11.013 18.3264 11.1509 18.2838 11.2717 18.2091C11.3925 18.1344 11.4922 18.03 11.5614 17.9059C11.6305 17.7818 11.6668 17.6421 11.6667 17.5V11.6667C11.6669 11.2537 11.8204 10.8554 12.0975 10.5492L18.1167 3.89167C18.2246 3.77213 18.2955 3.6239 18.3209 3.4649C18.3464 3.3059 18.3252 3.14294 18.2599 2.99573C18.1947 2.84851 18.0882 2.72335 17.9534 2.63538C17.8185 2.5474 17.661 2.50038 17.5 2.5H2.50003C2.33886 2.50006 2.18118 2.54685 2.04607 2.6347C1.91096 2.72255 1.80422 2.84769 1.73878 2.99497C1.67334 3.14225 1.65201 3.30534 1.67738 3.46449C1.70274 3.62364 1.77371 3.77203 1.88169 3.89167L7.90252 10.5492C8.17964 10.8554 8.33317 11.2537 8.33336 11.6667V16.6667Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {auditFilterOpen ? "Hide filter" : "Filter"}
                </button>
              </div>

              {/* Filter Panel */}
              {auditFilterOpen && (
                <div className="p-2.5 md:p-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Entity</label>
                        <Combobox
                          value={auditFilters.entity}
                          onChange={(value) => setAuditFilters({ ...auditFilters, entity: value })}
                          options={uniqueEntities.map((entity) => ({
                            value: entity,
                            label: entity,
                          }))}
                          placeholder="Select entity"
                          searchPlaceholder="Search entity..."
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Events</label>
                        <Combobox
                          value={auditFilters.event}
                          onChange={(value) => setAuditFilters({ ...auditFilters, event: value })}
                          options={uniqueEvents.map((event) => ({ value: event, label: event }))}
                          placeholder="Select event"
                          searchPlaceholder="Search event..."
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Date</label>
                        <div className="relative">
                          <input
                            id="audit-date-filter"
                            type="date"
                            className={`w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-inner-spin-button]:hidden ${!auditFilters.dateRange ? '[&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0' : ''}`}
                            value={auditFilters.dateRange}
                            onChange={(e) =>
                              setAuditFilters({ ...auditFilters, dateRange: e.target.value })
                            }
                          />
                          {!auditFilters.dateRange && (
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                              Select date
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => triggerPicker('audit-date-filter')}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 ${auditFilters.dateRange ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex flex-row gap-2 justify-end">
                      <Button
                        variant="outlineDestructive"
                        onClick={() => setAuditFilters({ entity: "", event: "", dateRange: "" })}
                        className="flex-1 md:flex-none"
                      >
                        Reset
                      </Button>
                      <Button
                        variant="outlinePrimary"
                        onClick={() => setAuditFilterOpen(false)}
                        className="flex-1 md:flex-none"
                      >
                        Apply filter
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Filters Display - Only show when filter panel is closed */}
              {!auditFilterOpen && (
                <div className="p-2.5 md:p-4">
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    {/* Filtered by row */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-sm font-medium text-gray-900">Filtered by:</span>
                      {hasActiveFilters ? (
                        <>
                          {auditFilters.event && (
                            <Badge
                              variant="secondary"
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 hover:bg-gray-200"
                            >
                              {auditFilters.event}
                              <button
                                onClick={() => setAuditFilters({ ...auditFilters, event: "" })}
                                className="ml-1 text-gray-500 hover:text-gray-700"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </Badge>
                          )}
                          {auditFilters.entity && (
                            <Badge
                              variant="secondary"
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 hover:bg-gray-200"
                            >
                              {auditFilters.entity}
                              <button
                                onClick={() => setAuditFilters({ ...auditFilters, entity: "" })}
                                className="ml-1 text-gray-500 hover:text-gray-700"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </Badge>
                          )}
                          {auditFilters.dateRange && (
                            <Badge
                              variant="secondary"
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 hover:bg-gray-200"
                            >
                              {auditFilters.dateRange}
                              <button
                                onClick={() => setAuditFilters({ ...auditFilters, dateRange: "" })}
                                className="ml-1 text-gray-500 hover:text-gray-700"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">No filters applied</span>
                      )}
                    </div>

                    {/* Results found row */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        Results found: <span className="font-semibold">{filteredAuditLogs.length}</span>
                      </span>
                      {hasActiveFilters && (
                        <button
                          onClick={() => setAuditFilters({ entity: "", event: "", dateRange: "" })}
                          className="text-muted-foreground flex items-center gap-1.5 text-sm hover:text-gray-700"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.4415 2.5H2.49905C2.33789 2.50006 2.1802 2.54685 2.04509 2.6347C1.90998 2.72255 1.80324 2.84769 1.7378 2.99497C1.67237 3.14225 1.65104 3.30534 1.6764 3.46449C1.70177 3.62364 1.77274 3.77203 1.88072 3.89167L7.90155 10.5492C8.17866 10.8554 8.33219 11.2537 8.33238 11.6667V16.6667C8.33231 16.8215 8.37539 16.9733 8.45679 17.1051C8.53819 17.2368 8.65469 17.3433 8.79322 17.4125L10.4599 18.2458C10.587 18.3093 10.7282 18.3393 10.8701 18.3329C11.012 18.3264 11.1499 18.2838 11.2707 18.2091C11.3916 18.1344 11.4913 18.03 11.5604 17.9059C11.6295 17.7818 11.6658 17.6421 11.6657 17.5V11.6667C11.6659 11.2537 11.8194 10.8554 12.0965 10.5492L12.4524 10.155M13.7491 2.91667L17.9158 7.08333M17.9158 2.91667L13.7491 7.08333"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Clear filter set
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {filteredAuditLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <svg
                    width="160"
                    height="84"
                    viewBox="0 0 160 84"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mb-6"
                  >
                    <path
                      d="M64.5723 15.7856C61.7319 15.7856 59.4294 18.0882 59.4294 20.9285C59.4294 23.7688 61.7319 26.0714 64.5723 26.0714H34.858C32.0177 26.0714 29.7151 28.3739 29.7151 31.2142C29.7151 34.0545 32.0177 36.3571 34.858 36.3571H53.1437C55.984 36.3571 58.2865 38.6596 58.2865 41.4999C58.2865 44.3403 55.984 46.6428 53.1437 46.6428H25.7151C22.8748 46.6428 20.5723 48.9453 20.5723 51.7856C20.5723 54.626 22.8748 56.9285 25.7151 56.9285H53.1437C50.3034 56.9285 48.0008 59.231 48.0008 62.0714C48.0008 64.9117 50.3034 67.2142 53.1437 67.2142H117.144C119.984 67.2142 122.287 64.9117 122.287 62.0714C122.287 59.231 119.984 56.9285 117.144 56.9285H115.715C112.875 56.9285 110.572 54.626 110.572 51.7856C110.572 48.9453 112.875 46.6428 115.715 46.6428H122.858C125.698 46.6428 128.001 44.3403 128.001 41.4999C128.001 38.6596 125.698 36.3571 122.858 36.3571H106.858C109.698 36.3571 112.001 34.0545 112.001 31.2142C112.001 28.3739 109.698 26.0714 106.858 26.0714H135.429C138.27 26.0714 140.572 23.7688 140.572 20.9285C140.572 18.0882 138.27 15.7856 135.429 15.7856H64.5723Z"
                      fill="#EDE9FE"
                    />
                    <path
                      d="M135.429 46.6428C138.27 46.6428 140.572 44.3403 140.572 41.4999C140.572 38.6596 138.27 36.3571 135.429 36.3571C132.589 36.3571 130.287 38.6596 130.287 41.4999C130.287 44.3403 132.589 46.6428 135.429 46.6428Z"
                      fill="#EDE9FE"
                    />
                    <rect
                      x="51.4277"
                      y="7.78564"
                      width="58.2857"
                      height="75.4286"
                      rx="4.57143"
                      fill="white"
                    />
                    <path
                      d="M62.8563 8.64279C62.8563 8.1694 62.4725 7.78564 61.9992 7.78564H55.9992C53.4744 7.78564 51.4277 9.83234 51.4277 12.3571V78.6428C51.4277 81.1675 53.4744 83.2142 55.9992 83.2142H105.142C107.667 83.2142 109.713 81.1675 109.713 78.6428V12.3571C109.713 9.83234 107.667 7.78564 105.142 7.78564H100.285C99.8115 7.78564 99.4277 8.1694 99.4277 8.64279C99.4277 9.11617 99.8115 9.49993 100.285 9.49993H105.142C106.72 9.49993 107.999 10.7791 107.999 12.3571V78.6428C107.999 80.2207 106.72 81.4999 105.142 81.4999H55.9992C54.4212 81.4999 53.142 80.2207 53.142 78.6428V12.3571C53.142 10.7791 54.4212 9.49993 55.9992 9.49993H61.9992C62.4725 9.49993 62.8563 9.11617 62.8563 8.64279Z"
                      fill="#7F22FE"
                    />
                    <path
                      d="M80 1.67871C82.4007 1.67884 84.4044 3.38388 84.8643 5.64941L84.9863 6.25H93.7139C94.562 6.25 95.25 6.93798 95.25 7.78613V10.0713C95.25 12.1817 93.539 13.8924 91.4287 13.8926H69.7139C67.6034 13.8925 65.8926 12.1818 65.8926 10.0713V7.78613C65.8926 6.93798 66.5806 6.25 67.4287 6.25H75.0127L75.1348 5.64941C75.5947 3.38379 77.5992 1.67871 80 1.67871Z"
                      fill="white"
                      stroke="#7F22FE"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M63.04 12.3572H58.2857C57.0234 12.3572 56 13.3806 56 14.6429V76.3572C56 77.6195 57.0234 78.6429 58.2857 78.6429H101.714C102.977 78.6429 104 77.6195 104 76.3572V66.0715C83.424 55.5063 76.9692 39.2047 76.58 16.9286H69.7143C67.6571 16.9286 65.943 16.6621 64.5715 15.2144C63.698 14.3869 63.2544 13.4344 63.04 12.3572Z"
                      fill="#EDE9FE"
                    />
                    <mask id="path-6-inside-1_283_41202" fill="white">
                      <rect x="59.4277" y="59.2144" width="8" height="8" rx="1.14286" />
                    </mask>
                    <rect
                      x="59.4277"
                      y="59.2144"
                      width="8"
                      height="8"
                      rx="1.14286"
                      stroke="#8E51FF"
                      strokeWidth="3"
                      mask="url(#path-6-inside-1_283_41202)"
                    />
                    <path
                      d="M72 63.2144H99.4286"
                      stroke="#8E51FF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <mask id="path-8-inside-2_283_41202" fill="white">
                      <rect x="59.4277" y="47.7856" width="8" height="8" rx="1.14286" />
                    </mask>
                    <rect
                      x="59.4277"
                      y="47.7856"
                      width="8"
                      height="8"
                      rx="1.14286"
                      stroke="#8E51FF"
                      strokeWidth="3"
                      mask="url(#path-8-inside-2_283_41202)"
                    />
                    <path
                      d="M72 51.7856H99.4286"
                      stroke="#8E51FF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <mask id="path-10-inside-3_283_41202" fill="white">
                      <rect x="59.4277" y="36.3569" width="8" height="8" rx="1.14286" />
                    </mask>
                    <rect
                      x="59.4277"
                      y="36.3569"
                      width="8"
                      height="8"
                      rx="1.14286"
                      stroke="#8E51FF"
                      strokeWidth="3"
                      mask="url(#path-10-inside-3_283_41202)"
                    />
                    <path
                      d="M72 40.3572H99.4286"
                      stroke="#8E51FF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <mask id="path-12-inside-4_283_41202" fill="white">
                      <rect x="59.4277" y="24.9287" width="8" height="8" rx="1.14286" />
                    </mask>
                    <rect
                      x="59.4277"
                      y="24.9287"
                      width="8"
                      height="8"
                      rx="1.14286"
                      stroke="#8E51FF"
                      strokeWidth="3"
                      mask="url(#path-12-inside-4_283_41202)"
                    />
                    <path
                      d="M72 28.9285H99.4286"
                      stroke="#8E51FF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">Logs list is empty</h3>
                  <p className="text-sm text-gray-600">
                    Wait until there's at least one audit log entry
                  </p>
                </div>
              ) : (
                /* Audit Logs Table - Horizontally Scrollable */
                (<div className="overflow-x-auto">
                  <table className="w-full min-w-[800px] text-sm">
                    <thead className="border-b border-gray-200 bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900">
                            Updated by
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900">
                            Entity
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900">
                            Event
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900">
                            Date
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left whitespace-nowrap">
                          <button className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900">
                            Old value
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-medium whitespace-nowrap text-gray-700">
                          New value
                        </th>
                        <th className="px-4 py-3 whitespace-nowrap"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredAuditLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900">
                            {log.updatedBy}
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900">
                            {log.entity}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge
                              variant="outline"
                              className={`rounded text-xs ${getEventBadgeClass(log.event)}`}
                            >
                              {log.event}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900">
                            {log.date}
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-600">
                            {log.oldValue}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">→</span>
                              <span
                                className={log.newValue === "-" ? "text-gray-600" : "text-gray-900"}
                              >
                                {log.newValue}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button className="text-gray-400 hover:text-gray-600">
                              <Info className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>)
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
